import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  BarChart3,
  Search,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { useStock } from "@/context/StockContext";
import { format, subDays, startOfDay, endOfDay } from "date-fns";

type SortField =
  | "name"
  | "unitsSold"
  | "revenue"
  | "profit"
  | "profitMargin"
  | "turnoverRate";
type SortDirection = "asc" | "desc";

interface ProductPerformance {
  productId: string;
  productName: string;
  category: string;
  unitsSold: number;
  revenue: number;
  cost: number;
  profit: number;
  profitMargin: number;
  currentStock: number;
  turnoverRate: number;
  averagePrice: number;
}

const ProductPerformanceReport: React.FC = () => {
  const { t } = useTranslation();
  const { products, sales } = useStock();

  const [dateRange, setDateRange] = useState("30"); // days
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>("revenue");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Calculate product performance metrics
  const productPerformance = useMemo(() => {
    const days = parseInt(dateRange);
    const startDate = startOfDay(subDays(new Date(), days));
    const endDate = endOfDay(new Date());

    // Filter sales within date range
    const filteredSales = sales.filter((sale) => {
      const saleDate = new Date(sale.createdAt);
      return saleDate >= startDate && saleDate <= endDate;
    });

    // Calculate metrics for each product
    const performanceMap = new Map<string, ProductPerformance>();

    filteredSales.forEach((sale) => {
      sale.items.forEach((item) => {
        const product = products.find((p) => p.id === item.productId);
        if (!product) return;

        const existing = performanceMap.get(item.productId) || {
          productId: item.productId,
          productName: product.name,
          category: product.category?.name || "Uncategorized",
          unitsSold: 0,
          revenue: 0,
          cost: 0,
          profit: 0,
          profitMargin: 0,
          currentStock: product.stockQuantity,
          turnoverRate: 0,
          averagePrice: 0,
        };

        const itemRevenue = item.unitPrice * item.quantity;
        const itemCost = (product.cost || 0) * item.quantity;
        const itemProfit = itemRevenue - itemCost;

        existing.unitsSold += item.quantity;
        existing.revenue += itemRevenue;
        existing.cost += itemCost;
        existing.profit += itemProfit;

        performanceMap.set(item.productId, existing);
      });
    });

    // Calculate derived metrics
    const performanceArray = Array.from(performanceMap.values()).map((perf) => {
      perf.profitMargin =
        perf.revenue > 0 ? (perf.profit / perf.revenue) * 100 : 0;
      perf.averagePrice =
        perf.unitsSold > 0 ? perf.revenue / perf.unitsSold : 0;

      // Turnover rate: units sold / (current stock + units sold)
      // If current stock is 0 and units sold > 0, turnover is 100%
      // If both are 0, turnover is 0%
      const totalAvailable = perf.currentStock + perf.unitsSold;
      perf.turnoverRate =
        totalAvailable > 0 ? perf.unitsSold / totalAvailable : 0;

      return perf;
    });

    return performanceArray;
  }, [products, sales, dateRange]);

  // Filter and sort
  const filteredAndSorted = useMemo(() => {
    let filtered = productPerformance;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const multiplier = sortDirection === "asc" ? 1 : -1;

      if (typeof aVal === "string" && typeof bVal === "string") {
        return aVal.localeCompare(bVal) * multiplier;
      }

      return ((aVal as number) - (bVal as number)) * multiplier;
    });

    return filtered;
  }, [productPerformance, searchTerm, sortField, sortDirection]);

  // Summary statistics
  const summary = useMemo(() => {
    return {
      totalRevenue: productPerformance.reduce((sum, p) => sum + p.revenue, 0),
      totalProfit: productPerformance.reduce((sum, p) => sum + p.profit, 0),
      totalUnitsSold: productPerformance.reduce(
        (sum, p) => sum + p.unitsSold,
        0
      ),
      averageProfitMargin:
        productPerformance.length > 0
          ? productPerformance.reduce((sum, p) => sum + p.profitMargin, 0) /
            productPerformance.length
          : 0,
    };
  }, [productPerformance]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field)
      return <ArrowUpDown className="h-4 w-4 opacity-30" />;
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            {t("stock.productPerformance.title")}
          </h2>
          <p className="text-sm text-gray-500">
            {t("stock.productPerformance.description")}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  {t("stock.productPerformance.totalRevenue")}
                </p>
                <p className="text-2xl font-bold">
                  {summary.totalRevenue.toFixed(3)} {t("common.currency")}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
                <DollarSign className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  {t("stock.productPerformance.totalProfit")}
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {summary.totalProfit.toFixed(3)} {t("common.currency")}
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full">
                <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  {t("stock.productPerformance.unitsSold")}
                </p>
                <p className="text-2xl font-bold">{summary.totalUnitsSold}</p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-full">
                <Package className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  {t("stock.productPerformance.avgProfitMargin")}
                </p>
                <p className="text-2xl font-bold">
                  {summary.averageProfitMargin.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-full">
                <BarChart3 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t("stock.productPerformance.searchProducts")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger>
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">
                    {t("stock.productPerformance.last7Days")}
                  </SelectItem>
                  <SelectItem value="30">
                    {t("stock.productPerformance.last30Days")}
                  </SelectItem>
                  <SelectItem value="90">
                    {t("stock.productPerformance.last90Days")}
                  </SelectItem>
                  <SelectItem value="365">
                    {t("stock.productPerformance.lastYear")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-sm">
                {filteredAndSorted.length}{" "}
                {t("stock.productPerformance.products")}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {t("stock.productPerformance.performanceTable")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                    <button
                      onClick={() => handleSort("name")}
                      className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {t("stock.productPerformance.product")}
                      <SortIcon field="name" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t("stock.category")}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                    <button
                      onClick={() => handleSort("unitsSold")}
                      className="flex items-center gap-1 ml-auto hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {t("stock.productPerformance.unitsSold")}
                      <SortIcon field="unitsSold" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                    <button
                      onClick={() => handleSort("revenue")}
                      className="flex items-center gap-1 ml-auto hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {t("stock.productPerformance.revenue")}
                      <SortIcon field="revenue" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                    <button
                      onClick={() => handleSort("profit")}
                      className="flex items-center gap-1 ml-auto hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {t("stock.productPerformance.profit")}
                      <SortIcon field="profit" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                    <button
                      onClick={() => handleSort("profitMargin")}
                      className="flex items-center gap-1 ml-auto hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {t("stock.productPerformance.profitMargin")}
                      <SortIcon field="profitMargin" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                    <button
                      onClick={() => handleSort("turnoverRate")}
                      className="flex items-center gap-1 ml-auto hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {t("stock.productPerformance.turnoverRate")}
                      <SortIcon field="turnoverRate" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredAndSorted.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      {t("stock.productPerformance.noData")}
                    </td>
                  </tr>
                ) : (
                  filteredAndSorted.map((product) => (
                    <tr
                      key={product.productId}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <td className="px-4 py-3 text-sm font-medium">
                        {product.productName}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant="outline">{product.category}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        {product.unitsSold}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">
                        {product.revenue.toFixed(3)} {t("common.currency")}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span
                          className={
                            product.profit >= 0
                              ? "text-green-600"
                              : "text-red-600"
                          }
                        >
                          {product.profit.toFixed(3)} {t("common.currency")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex items-center justify-end gap-1">
                          {product.profitMargin >= 0 ? (
                            <TrendingUp className="h-4 w-4 text-green-600" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-red-600" />
                          )}
                          <span
                            className={
                              product.profitMargin >= 0
                                ? "text-green-600"
                                : "text-red-600"
                            }
                          >
                            {product.profitMargin.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <Badge
                          variant={
                            product.turnoverRate > 0.5 ? "default" : "secondary"
                          }
                        >
                          {isNaN(product.turnoverRate) ||
                          !isFinite(product.turnoverRate)
                            ? "0%"
                            : `${(product.turnoverRate * 100).toFixed(0)}%`}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductPerformanceReport;
